import React, { useState, useEffect } from 'react';
import { investmentAPI } from '../../services/api';
import { Calculator, TrendingUp, Download, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';

const SummaryTab = ({ investmentId, currency, investment }) => {
  const [summary, setSummary] = useState(null);
  const [dateOfIRR, setDateOfIRR] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Display options
  const [displayFormat, setDisplayFormat] = useState('full');
  const [convertCurrency, setConvertCurrency] = useState(false);
  const [conversionRate, setConversionRate] = useState('');
  const [showConversionInput, setShowConversionInput] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, [investmentId]);

  const fetchSummary = async (irrDate = null) => {
    setLoading(true);
    try {
      const response = await investmentAPI.getSummary(investmentId, irrDate);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateIRR = () => {
    if (dateOfIRR) {
      fetchSummary(dateOfIRR);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return '0';
    
    let value = amount;
    
    if (convertCurrency && conversionRate) {
      value = value * parseFloat(conversionRate);
    }
    
    switch (displayFormat) {
      case 'crores':
        return (value / 10000000).toFixed(2);
      case 'millions':
        return (value / 1000000).toFixed(2);
      default:
        return value.toLocaleString();
    }
  };

  const getDisplayCurrency = () => {
    if (convertCurrency && conversionRate) {
      return currency === 'USD' ? 'INR' : 'USD';
    }
    return currency;
  };

  const getDisplayUnit = () => {
    switch (displayFormat) {
      case 'crores':
        return 'Cr';
      case 'millions':
        return 'M';
      default:
        return '';
    }
  };

  const handleExport = async (format) => {
    if (format === 'pdf') {
      window.print();
    } else if (format === 'excel') {
      // Prepare summary data for Excel export
      const exportData = [
        { Metric: 'Total Investment Made', Value: formatAmount(summary?.total_investment_made), Currency: getDisplayCurrency(), Unit: getDisplayUnit() },
        { Metric: 'Total Realisation', Value: formatAmount(summary?.total_realisation), Currency: getDisplayCurrency(), Unit: getDisplayUnit() },
        { Metric: 'Current Stake Value', Value: formatAmount(summary?.value_of_fund_stake_recent), Currency: getDisplayCurrency(), Unit: getDisplayUnit() },
        { Metric: 'Current % Shareholding', Value: summary?.total_percentage_shareholding ? summary.total_percentage_shareholding.toFixed(2) : '0.00', Currency: '%', Unit: '' },
        { Metric: 'Multiple Generated', Value: summary?.multiple_generated?.toFixed(2) || '0', Currency: 'x', Unit: '' },
        { Metric: 'Investment IRR', Value: summary?.irr !== null && summary?.irr !== undefined ? summary.irr : 'Not Calculated', Currency: summary?.irr ? '%' : '', Unit: '' },
      ];

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Investment Summary');

      // Auto-size columns
      ws['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 10 }, { wch: 10 }];

      // Generate file
      XLSX.writeFile(wb, `${investment?.portfolio_company_name || 'Investment'}_Summary.xlsx`);
    } else {
      alert(`Export to ${format.toUpperCase()} - Feature coming soon!`);
    }
  };

  const toggleCurrencyConversion = () => {
    if (!convertCurrency) {
      setShowConversionInput(true);
    } else {
      setConvertCurrency(false);
      setConversionRate('');
      setShowConversionInput(false);
    }
  };

  const applyConversion = () => {
    if (conversionRate && parseFloat(conversionRate) > 0) {
      setConvertCurrency(true);
      setShowConversionInput(false);
    } else {
      alert('Please enter a valid conversion rate');
    }
  };

  if (loading && !summary) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const displayCurrency = getDisplayCurrency();
  const displayUnit = getDisplayUnit();

  return (
    <div id="summary-content">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Investment Summary</h3>
        
        <div className="flex items-center space-x-2">
          <select
            value={displayFormat}
            onChange={(e) => setDisplayFormat(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="full">Full Amount</option>
            <option value="crores">Crores</option>
            <option value="millions">Millions</option>
          </select>

          <button
            onClick={toggleCurrencyConversion}
            className={`flex items-center px-3 py-2 rounded-lg text-sm ${
              convertCurrency
                ? 'bg-green-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title="Convert Currency"
          >
            <RefreshCw size={16} className="mr-1" />
            {convertCurrency ? `${currency} → ${displayCurrency}` : 'Convert Currency'}
          </button>

          <div className="relative group">
            <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Download size={16} className="mr-1" />
              Export
            </button>
            <div className="hidden group-hover:block absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-10">
              <button
                onClick={() => handleExport('excel')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                Excel
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                PDF
              </button>
              <button
                onClick={() => handleExport('docx')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                DOCX
              </button>
              <button
                onClick={() => handleExport('pptx')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                PPTX
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConversionInput && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-semibold mb-3">Currency Conversion Rate</h4>
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium">
              1 {currency} =
            </label>
            <input
              type="number"
              step="0.0001"
              value={conversionRate}
              onChange={(e) => setConversionRate(e.target.value)}
              placeholder="Enter rate"
              className="px-3 py-2 border rounded-lg w-32"
            />
            <label className="text-sm font-medium">
              {currency === 'USD' ? 'INR' : 'USD'}
            </label>
            <button
              onClick={applyConversion}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Apply
            </button>
            <button
              onClick={() => setShowConversionInput(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {convertCurrency && conversionRate && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm">
            <span className="font-medium">Conversion Rate:</span> 1 {currency} = {conversionRate} {displayCurrency}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-blue-50 p-6 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Total Investment Made</p>
          <p className="text-2xl font-bold text-blue-600">
            {displayCurrency} {formatAmount(summary?.total_investment_made)} {displayUnit}
          </p>
        </div>

        <div className="bg-green-50 p-6 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Total Realisation</p>
          <p className="text-2xl font-bold text-green-600">
            {displayCurrency} {formatAmount(summary?.total_realisation)} {displayUnit}
          </p>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Current Stake Value</p>
          <p className="text-2xl font-bold text-purple-600">
            {displayCurrency} {formatAmount(summary?.value_of_fund_stake_recent)} {displayUnit}
          </p>
          {summary?.recent_fmv_date && (
            <p className="text-xs text-gray-500 mt-1">As of {summary.recent_fmv_date}</p>
          )}
        </div>

        <div className="bg-orange-50 p-6 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Current % Shareholding</p>
          <p className="text-2xl font-bold text-orange-600">
            {summary?.total_percentage_shareholding ? summary.total_percentage_shareholding.toFixed(2) : '0.00'}%
          </p>
        </div>

        <div className="bg-indigo-50 p-6 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Multiple Generated</p>
          <p className="text-2xl font-bold text-indigo-600">
            {summary?.multiple_generated?.toFixed(2) || '0'}x
          </p>
          {summary?.recent_fmv_date && (
            <p className="text-xs text-gray-500 mt-1">As of {summary.recent_fmv_date}</p>
          )}
        </div>

        <div className="bg-pink-50 p-6 rounded-lg">
          <div className="flex items-center mb-1">
            <TrendingUp size={20} className="text-pink-600 mr-2" />
            <p className="text-sm text-gray-600">Investment IRR</p>
          </div>
          {summary?.irr !== null && summary?.irr !== undefined ? (
            <p className="text-2xl font-bold text-pink-600">{summary.irr}%</p>
          ) : (
            <p className="text-lg text-gray-500">Calculate below</p>
          )}
          {summary?.irr_error && (
            <p className="text-xs text-red-600 mt-1">{summary.irr_error}</p>
          )}
        </div>
      </div>

      <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Calculator size={24} className="text-blue-600 mr-2" />
          <h4 className="text-lg font-semibold">Calculate IRR (XIRR)</h4>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Enter a date that matches an existing Fair Market Value (FMV) record to calculate the Investment IRR.
        </p>
        <div className="flex space-x-4">
          <input
            type="date"
            value={dateOfIRR}
            onChange={(e) => setDateOfIRR(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="Select FMV date"
          />
          <button
            onClick={calculateIRR}
            disabled={!dateOfIRR || loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
          >
            {loading ? 'Calculating...' : 'Calculate IRR'}
          </button>
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #summary-content, #summary-content * {
            visibility: visible;
          }
          #summary-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button, select {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SummaryTab;
